import { Link } from 'react-router-dom';
import { BookOpen, Feather, Shield, Star, ArrowRight, CheckCircle, Users, Clock, Award } from 'lucide-react';
import SeoHead from '../components/SeoHead';
import NewsletterSignup from '../components/NewsletterSignup';

const FEATURES = [
  { icon: Feather, title: 'AI-Powered Writing', desc: 'Our AI creates multiple drafts tailored to your vision, voice, and genre.' },
  { icon: Shield, title: 'Your Story, Protected', desc: 'Full copyright ownership. Your name on the cover. Your story, always.' },
  { icon: Clock, title: 'Fast Turnaround', desc: 'From concept to completed manuscript in as little as 14 days.' },
  { icon: Award, title: 'Publication Ready', desc: 'Formatted for Amazon KDP, IngramSpark, and all major platforms.' },
  { icon: Users, title: 'Dedicated Support', desc: 'A real team reviews, refines, and polishes every manuscript.' },
  { icon: Star, title: 'Satisfaction Guaranteed', desc: 'Multiple revision rounds until your book is exactly right.' },
];

const TESTIMONIALS = [
  { name: 'Sarah Mitchell', role: 'Romance Author', text: "I had an idea for years but never the time. AI Ghostwriter Pro turned my dream into a published novel in under a month.", rating: 5 },
  { name: 'James Rivera', role: 'Business Coach', text: "The quality blew me away. My book became a cornerstone of my coaching brand. Worth every penny.", rating: 5 },
  { name: 'Dr. Angela Brooks', role: 'Non-Fiction Writer', text: "Professional, fast, and the writing quality was exceptional. I've already ordered my second book.", rating: 5 },
];

const STATS = [
  { value: '500+', label: 'Books Written' },
  { value: '98%', label: 'Satisfaction Rate' },
  { value: '14', label: 'Day Delivery' },
  { value: '$149/mo', label: 'Starting Price' },
];

export default function AIGhostwriterLanding() {
  return (
    <>
      <SeoHead
        title="AI Ghostwriter Pro — Hire a Professional Book Ghostwriter | Premium Ghostwriting Service"
        description="Hire a professional AI ghostwriter to write your book. Premium ghostwriting service for novels, non-fiction, memoirs, and business books. Monthly plans from $149/mo with 7-day free trial. Full copyright ownership, professional formatting, unlimited revisions."
        keywords="hire a ghostwriter, professional book ghostwriting service, AI ghostwriter for hire, ghostwriter cost, how to hire a ghostwriter for a book, best ghostwriting service online, affordable ghostwriter, book writing service, novel ghostwriter, memoir ghostwriter, business book ghostwriter, ghostwriting rates, ghostwriter near me"
        schemas={[
          {
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": "AI Ghostwriter Pro — Premium Book Ghostwriting Service",
            "description": "Professional AI-powered ghostwriting service for aspiring authors",
            "url": "https://aighostwriterpro.com",
            "isPartOf": {"@type": "WebSite", "name": "AI Ghostwriter Pro", "url": "https://aighostwriterpro.com"}
          }
        ]}
      />

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center gold-glow noise-overlay" data-testid="hero-section">
        <div className="relative z-10 max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20 pt-28 pb-16 md:pt-32 md:pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-12 items-center">
            <div className="lg:col-span-7">
              <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4 md:mb-6 animate-fade-in-up">Premium Ghostwriting Service</p>
              <h1 className="font-serif text-4xl sm:text-5xl md:text-7xl lg:text-8xl leading-[0.9] tracking-tight text-foreground mb-6 md:mb-8 animate-fade-in-up animation-delay-100">
                Your Story.<br />
                <span className="text-primary">Professionally</span><br />
                Written.
              </h1>
              <p className="font-sans text-base sm:text-lg md:text-xl text-muted-foreground leading-relaxed max-w-xl mb-8 md:mb-10 animate-fade-in-up animation-delay-200">
                From a spark of an idea to a published masterpiece. Our AI-powered service delivers publication-ready manuscripts in as little as 14 days.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 animate-fade-in-up animation-delay-300">
                <Link to="/pricing" data-testid="hero-cta-pricing" className="inline-flex items-center gap-2 rounded-sm px-8 py-4 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black hover:shadow-[0_0_25px_rgba(255,255,255,0.5)]">
                  View Packages <ArrowRight className="w-4 h-4" />
                </Link>
                <Link to="/how-it-works" data-testid="hero-cta-how" className="inline-flex items-center gap-2 rounded-sm px-8 py-4 border border-white/20 text-foreground font-semibold tracking-wide hover:border-primary hover:text-primary transition-all duration-300">
                  How It Works
                </Link>
              </div>
              <p className="text-xs text-muted-foreground mt-4 animate-fade-in-up animation-delay-400" data-testid="hero-no-cc">7-day free trial — no credit card required</p>
            </div>
            <div className="lg:col-span-5 hidden lg:block animate-fade-in-up animation-delay-400">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-t from-background via-transparent to-transparent z-10" />
                <img
                  src="https://images.unsplash.com/photo-1700235984672-9b9cd925fe35?q=80&w=2000&auto=format&fit=crop"
                  alt="Professional ghostwriting workspace with open manuscript for aspiring authors who want to hire a book ghostwriter"
                  className="w-full h-[500px] object-cover rounded-sm opacity-60"
                />
                <div className="absolute bottom-8 left-8 right-8 z-20">
                  <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-sm p-6">
                    <p className="font-serif text-lg text-foreground italic">"There is nothing to writing. All you do is sit down and bleed."</p>
                    <p className="text-sm text-primary mt-2">— Ernest Hemingway</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="border-y border-white/5 py-8 md:py-12" data-testid="stats-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {STATS.map((stat, i) => (
              <div key={i} className="text-center">
                <p className="font-serif text-3xl md:text-4xl text-primary">{stat.value}</p>
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground mt-2">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-24 lg:py-32" data-testid="features-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">Why Choose Us</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight">Everything You Need to<br /><span className="text-primary">Publish Your Book</span></h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {FEATURES.map((f, i) => (
              <div key={i} className="bg-white/5 border border-white/10 backdrop-blur-md rounded-sm p-8 hover:border-primary/50 transition-colors duration-500 group" data-testid={`feature-card-${i}`}>
                <f.icon className="w-8 h-8 text-primary mb-4 group-hover:scale-110 transition-transform" strokeWidth={1.5} />
                <h3 className="font-serif text-xl mb-3 text-foreground">{f.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Preview */}
      <section className="py-16 md:py-24 lg:py-32 gold-glow" data-testid="pricing-preview">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20 text-center">
          <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">Simple Pricing</p>
          <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight mb-6">Plans Starting at <span className="text-primary">$149/mo</span></h2>
          <p className="text-muted-foreground max-w-2xl mx-auto mb-10 leading-relaxed">Choose the plan that fits your vision. Flexible monthly or annual billing. Every plan includes AI-powered writing, professional formatting, and full copyright ownership.</p>
          <Link to="/pricing" data-testid="pricing-preview-cta" className="inline-flex items-center gap-2 rounded-sm px-8 py-4 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black hover:shadow-[0_0_25px_rgba(255,255,255,0.5)]">
            See All Packages <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-16 md:py-24 lg:py-32" data-testid="testimonials-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20">
          <div className="text-center mb-12 md:mb-16">
            <p className="font-sans uppercase tracking-[0.2em] text-xs font-bold text-primary mb-4">Testimonials</p>
            <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight">What Our <span className="text-primary">Authors</span> Say</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 md:gap-8">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="bg-white/5 border border-white/10 backdrop-blur-md rounded-sm p-8" data-testid={`testimonial-${i}`}>
                <div className="flex gap-1 mb-4">
                  {[...Array(t.rating)].map((_, j) => <Star key={j} className="w-4 h-4 text-primary fill-primary" />)}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed mb-6 italic">"{t.text}"</p>
                <div>
                  <p className="text-sm font-semibold text-foreground">{t.name}</p>
                  <p className="text-xs text-muted-foreground">{t.role}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20 border-t border-white/5" data-testid="newsletter-section">
        <div className="max-w-lg mx-auto px-6 md:px-12">
          <NewsletterSignup source="landing-page" />
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 lg:py-32 border-t border-white/5" data-testid="cta-section">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 md:px-12 lg:px-20 text-center">
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl leading-tight tracking-tight mb-4 md:mb-6">Ready to Write Your<br /><span className="text-primary">Masterpiece?</span></h2>
          <p className="text-muted-foreground max-w-xl mx-auto mb-8 md:mb-10 leading-relaxed text-sm md:text-base">Join hundreds of authors who turned their ideas into published books with AI Ghostwriter Pro.</p>
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4">
            <Link to="/pricing" data-testid="cta-start" className="inline-flex items-center gap-2 rounded-sm px-8 py-4 bg-primary text-primary-foreground font-semibold tracking-wide transition-all duration-300 shadow-[0_0_15px_rgba(212,175,55,0.3)] hover:bg-white hover:text-black hover:shadow-[0_0_25px_rgba(255,255,255,0.5)]">
              Start Your Book Today <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" data-testid="cta-contact" className="inline-flex items-center gap-2 rounded-sm px-8 py-4 border border-white/20 text-foreground font-semibold tracking-wide hover:border-primary hover:text-primary transition-all duration-300">
              Talk to Us
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
